import create from 'zustand';
import { immer } from 'zustand/middleware/immer';
import type { Block, ChildBlock, ColumnsBlock, CoverImage } from '@staty/shared/schemas/block';
import { htmlToBlocks, hasBlocks, newBlockId } from '@staty/shared/utils/blockUtils';
import { extractCover, cleanBody } from '@staty/shared/utils/postContent';

/**
 * Working state of the canvas editor (ADR 0033).
 *
 * The stores under src/store own SERVER state; this store owns the DRAFTS
 * being edited on the canvas — for POSTS (feed cards) and LAYOUTS (custom
 * pages, the org-home fallback page, division callouts) — plus selection and
 * autosave bookkeeping. useAutosave flushes dirty drafts back through
 * postStore/layoutStore.
 *
 * Entities are addressed by { kind, id } refs; block ops work on either
 * draft type (both carry `blocks`).
 */

export type EntityKind = 'post' | 'layout';

export interface EntityRef {
  kind: EntityKind;
  id: string;
}

export const entityKey = (ref: EntityRef): string => `${ref.kind}:${ref.id}`;

export interface PostEntity {
  _id: string;
  title?: string;
  html?: string;
  blocks?: Block[];
  coverImage?: CoverImage | null;
  publishedAt?: string | Date;
  showTitle?: boolean;
  showDate?: boolean;
  status?: 'published' | 'draft';
  // Pinned post (INT-0048): at most one per org; first on /home and here.
  pinned?: boolean;
  organization?: string;
  created?: string;
}

/** The pinned post first, everything else in the given (date) order. */
export function orderPinnedFirst<T extends { pinned?: boolean }>(posts: T[]): T[] {
  const pinned = posts.find((p) => p && p.pinned === true);
  return pinned ? [pinned, ...posts.filter((p) => p !== pinned)] : posts;
}

export interface LayoutEntity {
  _id: string;
  name?: string;
  html?: string;
  blocks?: Block[];
  pageLocation?: string;
  tier?: string;
  order?: number;
  active?: boolean;
  organization?: string;
  league?: string;
  division?: string;
}

export interface PostDraft {
  kind: 'post';
  blocks: Block[];
  coverImage: CoverImage | null;
  title: string;
  publishedAt: string | null; // ISO yyyy-mm-dd for the date input
  showTitle: boolean;
  showDate: boolean;
}

export interface LayoutDraft {
  kind: 'layout';
  blocks: Block[];
  name: string;
  order: number;
  // Public nav filters `active !== false`; the schema DEFAULT is false, so
  // this is a real, user-facing switch ("Show in navigation").
  active: boolean;
}

export type Draft = PostDraft | LayoutDraft;

export type SaveState = 'idle' | 'dirty' | 'saving' | 'saved' | 'error';

/**
 * Addresses a column CELL inside a top-level columns block (depth-1, matching
 * the schema). Block ops that receive a colAddr operate on that cell's child
 * list instead of the entity's top-level list.
 */
export interface ColAddr {
  blockId: string;
  columnId: string;
}

// The canvas stage shows either the posts feed or one layout surface.
export type Surface = { kind: 'feed' } | { kind: 'layout'; id: string };

/**
 * Legacy html → draft, using the SAME semantics as the html→blocks migration
 * script: the lead image becomes the explicit cover, the cleaned remainder
 * becomes one richtext block. This is what keeps a lazily-converted post
 * rendering identically before and after its first canvas edit.
 */
export function draftFromPost(post: PostEntity): PostDraft {
  let blocks: Block[];
  // Guard against src-less cover objects: documents created before the
  // model pinned `coverImage: default undefined` carry a stamped `{alt:''}`,
  // which must read as "no cover" (sending it back would 400 on validation).
  let coverImage: CoverImage | null = post.coverImage?.src ? post.coverImage : null;
  if (hasBlocks(post)) {
    blocks = post.blocks as Block[];
  } else {
    const { coverSrc, bodyHtml } = extractCover(post.html || '');
    if (coverSrc && !coverImage) coverImage = { src: coverSrc, alt: post.title || '' };
    blocks = htmlToBlocks(cleanBody(bodyHtml));
  }
  return {
    kind: 'post',
    blocks,
    coverImage,
    title: post.title || '',
    publishedAt: post.publishedAt
      ? new Date(post.publishedAt).toISOString().slice(0, 10)
      : null,
    showTitle: post.showTitle !== false,
    showDate: post.showDate !== false,
  };
}

/**
 * Layout lazy conversion: html verbatim into one richtext block — NO
 * cleanBody, because the public layout renderers inject the html untouched;
 * the converted draft must render exactly what visitors already see.
 */
export function draftFromLayout(layout: LayoutEntity): LayoutDraft {
  return {
    kind: 'layout',
    blocks: hasBlocks(layout) ? (layout.blocks as Block[]) : htmlToBlocks(layout.html || ''),
    name: layout.name || '',
    order: layout.order ?? 0,
    active: layout.active !== false,
  };
}

/**
 * Deep-copy a block with fresh ids everywhere (top-level id, column ids, and
 * nested child ids) — a duplicate must never share an id with the original,
 * or selection/dnd/React keys all alias.
 */
export function cloneWithNewIds(block: Block | ChildBlock): Block {
  const copy: Block = JSON.parse(JSON.stringify(block));
  copy.id = newBlockId();
  if (copy.type === 'columns') {
    for (const col of copy.columns) {
      col.id = newBlockId();
      for (const child of col.blocks) child.id = newBlockId();
    }
  }
  return copy;
}

// Resolve the list a colAddr points at (or the top-level list when absent).
// Returns null when the address no longer exists (block deleted mid-flight).
function resolveList(draft: Draft, colAddr?: ColAddr): Block[] | ChildBlock[] | null {
  if (!colAddr) return draft.blocks;
  const host = draft.blocks.find(
    (b): b is ColumnsBlock => b.id === colAddr.blockId && b.type === 'columns'
  );
  const col = host?.columns.find((c) => c.id === colAddr.columnId);
  return col ? col.blocks : null;
}

interface EditorState {
  surface: Surface;
  selected: EntityRef | null;
  selectedBlockId: string | null;
  drafts: Record<string, Draft>; // keyed by entityKey(ref)
  dirtyKeys: string[];
  // Bumps on EVERY edit (not just the first that dirties a draft) so the
  // autosave debounce re-arms per keystroke — "1.5s after the LAST change".
  editSeq: number;
  saveState: SaveState;
  saveError: string | null;

  showFeed: () => void;
  selectPost: (post: PostEntity) => void;
  selectLayout: (layout: LayoutEntity) => void;
  selectBlock: (blockId: string | null) => void;
  patchDraft: (ref: EntityRef, patch: Partial<PostDraft> | Partial<LayoutDraft>) => void;
  updateBlock: (ref: EntityRef, blockId: string, patch: Partial<Block>, colAddr?: ColAddr) => void;
  insertBlock: (ref: EntityRef, index: number, block: Block | ChildBlock, colAddr?: ColAddr) => void;
  removeBlock: (ref: EntityRef, blockId: string, colAddr?: ColAddr) => void;
  duplicateBlock: (ref: EntityRef, blockId: string, colAddr?: ColAddr) => void;
  moveBlock: (ref: EntityRef, fromIndex: number, toIndex: number, colAddr?: ColAddr) => void;
  takeDirty: () => string[];
  requeueDirty: (keys: string[]) => void;
  setSaveState: (s: SaveState, error?: string | null) => void;
  forgetEntity: (ref: EntityRef) => void;
  reset: () => void;
}

function markDirty(state: {
  dirtyKeys: string[];
  editSeq: number;
  saveState: SaveState;
}, key: string) {
  if (!state.dirtyKeys.includes(key)) state.dirtyKeys.push(key);
  state.editSeq += 1;
  state.saveState = 'dirty';
}

const useEditorStore = create<EditorState>()(
  immer((set, get) => ({
    surface: { kind: 'feed' },
    selected: null,
    selectedBlockId: null,
    drafts: {},
    dirtyKeys: [],
    editSeq: 0,
    saveState: 'idle',
    saveError: null,

    showFeed: () =>
      set((state) => {
        state.surface = { kind: 'feed' };
        state.selected = null;
        state.selectedBlockId = null;
      }),

    selectPost: (post) =>
      set((state) => {
        state.surface = { kind: 'feed' };
        state.selected = { kind: 'post', id: post._id };
        state.selectedBlockId = null;
        const key = `post:${post._id}`;
        // Never rebuild an existing draft — that would discard unsaved edits
        // (and un-converted legacy state must convert exactly once).
        if (!state.drafts[key]) {
          state.drafts[key] = draftFromPost(post);
        }
      }),

    selectLayout: (layout) =>
      set((state) => {
        state.surface = { kind: 'layout', id: layout._id };
        state.selected = { kind: 'layout', id: layout._id };
        state.selectedBlockId = null;
        const key = `layout:${layout._id}`;
        if (!state.drafts[key]) {
          state.drafts[key] = draftFromLayout(layout);
        }
      }),

    selectBlock: (blockId) =>
      set((state) => {
        state.selectedBlockId = blockId;
      }),

    patchDraft: (ref, patch) =>
      set((state) => {
        const draft = state.drafts[entityKey(ref)];
        if (!draft) return;
        Object.assign(draft, patch);
        markDirty(state, entityKey(ref));
      }),

    updateBlock: (ref, blockId, patch, colAddr) =>
      set((state) => {
        const draft = state.drafts[entityKey(ref)];
        if (!draft) return;
        const list = resolveList(draft, colAddr);
        const block = list?.find((b) => b.id === blockId);
        if (!block) return;
        Object.assign(block, patch);
        markDirty(state, entityKey(ref));
      }),

    insertBlock: (ref, index, block, colAddr) =>
      set((state) => {
        const draft = state.drafts[entityKey(ref)];
        if (!draft) return;
        const list = resolveList(draft, colAddr);
        if (!list) return;
        const at = Math.max(0, Math.min(index, list.length));
        (list as Block[]).splice(at, 0, block as Block);
        state.selectedBlockId = block.id;
        markDirty(state, entityKey(ref));
      }),

    removeBlock: (ref, blockId, colAddr) =>
      set((state) => {
        const draft = state.drafts[entityKey(ref)];
        if (!draft) return;
        const list = resolveList(draft, colAddr);
        if (!list) return;
        const i = list.findIndex((b) => b.id === blockId);
        if (i === -1) return;
        list.splice(i, 1);
        if (state.selectedBlockId === blockId) state.selectedBlockId = null;
        markDirty(state, entityKey(ref));
      }),

    duplicateBlock: (ref, blockId, colAddr) =>
      set((state) => {
        const draft = state.drafts[entityKey(ref)];
        if (!draft) return;
        const list = resolveList(draft, colAddr);
        if (!list) return;
        const i = list.findIndex((b) => b.id === blockId);
        if (i === -1) return;
        const copy = cloneWithNewIds(list[i]);
        (list as Block[]).splice(i + 1, 0, copy);
        state.selectedBlockId = copy.id;
        markDirty(state, entityKey(ref));
      }),

    moveBlock: (ref, fromIndex, toIndex, colAddr) =>
      set((state) => {
        const draft = state.drafts[entityKey(ref)];
        if (!draft) return;
        const list = resolveList(draft, colAddr);
        if (!list) return;
        if (
          fromIndex < 0 || fromIndex >= list.length ||
          toIndex < 0 || toIndex >= list.length ||
          fromIndex === toIndex
        ) {
          return;
        }
        const [moved] = list.splice(fromIndex, 1);
        (list as Block[]).splice(toIndex, 0, moved as Block);
        markDirty(state, entityKey(ref));
      }),

    // Autosave claims the dirty set atomically; on failure it hands the keys
    // back so no edit is ever silently dropped.
    takeDirty: () => {
      const keys = get().dirtyKeys;
      set((state) => {
        state.dirtyKeys = [];
      });
      return keys;
    },

    requeueDirty: (keys) =>
      set((state) => {
        for (const key of keys) {
          if (!state.dirtyKeys.includes(key)) state.dirtyKeys.push(key);
        }
        // The retry signal. take→fail→requeue nets dirtyKeys.length back to
        // its pre-flush value inside one React batch, so WITHOUT this bump
        // the autosave effect's deps never change and no retry timer is
        // armed — a failed save would sit unsaved until the next keystroke.
        if (keys.length > 0) state.editSeq += 1;
      }),

    setSaveState: (s, error = null) =>
      set((state) => {
        state.saveState = s;
        state.saveError = error;
      }),

    forgetEntity: (ref) =>
      set((state) => {
        const key = entityKey(ref);
        delete state.drafts[key];
        state.dirtyKeys = state.dirtyKeys.filter((k) => k !== key);
        if (state.selected && entityKey(state.selected) === key) {
          state.selected = null;
          state.selectedBlockId = null;
        }
        if (state.surface.kind === 'layout' && state.surface.id === ref.id) {
          state.surface = { kind: 'feed' };
        }
      }),

    reset: () =>
      set((state) => {
        state.surface = { kind: 'feed' };
        state.selected = null;
        state.selectedBlockId = null;
        state.drafts = {};
        state.dirtyKeys = [];
        state.editSeq = 0;
        state.saveState = 'idle';
        state.saveError = null;
      }),
  }))
);

export default useEditorStore;
